import React, { useEffect, useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { AIDE_TASK_STATUSES } from '../../../../../dtos/patientmr/charting/aide-tasks.dto';
import { CLINICAL_TEMPLATES } from '../../../../../constants/clinicalTemplates';
import { disciplineForProviderType, getDisciplineEntry } from '../../../../../constants/disciplines';
import { CarePlansService } from '../../../../../services/patientmr/careplans.service';
import type { AideTasksFormData, AideTaskFormRow } from '../../types';
import { FieldsProps, InputField, Segmented, SelectField, TextAreaField } from './fieldParts';

export const TASK_STATUS_LABELS: Record<string, string> = { DONE: 'Done', REFUSED: 'Refused', NOT_NEEDED: 'Not needed' };

/** Task fields of a discipline's plan template: its multiselect fields whose key ends in "Tasks" */
export const planTaskKeys = (discipline: string): string[] => {
  const templateId = getDisciplineEntry(discipline)?.templates?.carePlan;
  const template = CLINICAL_TEMPLATES.find((t) => t.id === templateId);
  if (!template) return [];
  return template.sections.flatMap((s) => s.fields).filter((f) => f.type === 'multiselect' && /Tasks$/.test(f.key || '')).map((f) => f.key as string);
};

/** Tasks listed on the discipline's newest complete plan ([] when none or on error) */
export async function loadPlannedTasks(patientId: string, discipline: string): Promise<string[]> {
  const keys = planTaskKeys(discipline);
  if (!keys.length) return [];
  const { carePlans } = await CarePlansService.getCarePlans(patientId);
  const mine = carePlans
    .filter((p) => String(CarePlansService.mapUiDisciplineToApiDiscipline(p.discipline)).toUpperCase() === discipline
      && ['COMPLETE', 'CURRENT', 'SUBMITTED', 'QA'].includes(String(p.status).toUpperCase()))
    .sort((a, b) => Date.parse(b.date) - Date.parse(a.date));
  if (!mine.length) return [];
  const detail = await CarePlansService.getCarePlanDetails(patientId, mine[0].carePlanId);
  const data = (detail?.data || {}) as Record<string, unknown>;
  const tasks = keys.flatMap((k) => (Array.isArray(data[k]) ? (data[k] as unknown[]).map(String) : []));
  return [...new Set(tasks.filter(Boolean))];
}

/**
 * Phase 20D aide / attendant task note. Tasks come from the plan (when one is on file) and can be
 * added by hand; attendant visits show minutes per task.
 */
const AideTasksFields: React.FC<FieldsProps<AideTasksFormData> & { discipline?: string | null; patientId?: string }> = ({
  value, onChange, errors, showRequired, isMobile, disabled, discipline, patientId,
}) => {
  const mapped = disciplineForProviderType(discipline) || '';
  const allowed = Boolean(getDisciplineEntry(mapped)?.aideTasks);
  const attendant = mapped === 'ATTENDANT';
  const [loaded, setLoaded] = useState(false);

  // Prefill an empty note from the plan once
  useEffect(() => {
    if (!allowed || !patientId || loaded || value.tasks.some((t) => t.task.trim())) return undefined;
    let cancelled = false;
    loadPlannedTasks(patientId, mapped)
      .then((tasks) => {
        if (cancelled) return;
        setLoaded(true);
        if (tasks.length) onChange({ ...value, tasks: tasks.map((task) => ({ task, status: '', minutes: '', note: '' })) });
      })
      .catch(() => { if (!cancelled) setLoaded(true); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowed, patientId, mapped]);

  if (!allowed) {
    return <p className="text-sm text-gray-600" data-testid="aide-tasks-not-allowed">An aide task note can only be added on an aide or attendant visit.</p>;
  }
  const common = { isMobile, disabled, showRequired, labelClassName: 'text-lime-800' };
  const setTask = (i: number, patch: Partial<AideTaskFormRow>) =>
    onChange({ ...value, tasks: value.tasks.map((t, j) => (j === i ? { ...t, ...patch } : t)) });
  const totalMinutes = value.tasks.reduce((n, t) => n + (t.status === 'DONE' ? Number(t.minutes) || 0 : 0), 0);

  return (
    <div className="space-y-3" data-testid="aide-tasks-fields">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-lime-800">Tasks</span>
        {attendant && <span className="text-xs text-gray-600" data-testid="aide-tasks-minutes">{totalMinutes} minutes of tasks done</span>}
      </div>
      {value.tasks.map((t, i) => (
        <div key={i} className={`grid grid-cols-1 items-end gap-2 rounded border border-gray-200 p-2 ${attendant ? 'md:grid-cols-[1fr_9rem_6rem_auto]' : 'md:grid-cols-[1fr_9rem_auto]'}`} data-testid={`aide-task-${i}`}>
          <InputField {...common} id={`aide-task-${i}-task`} label="Task" value={t.task} onChange={(v) => setTask(i, { task: v })} error={errors[`tasks[${i}].task`]} />
          <SelectField {...common} id={`aide-task-${i}-status`} label="Status" required
            options={AIDE_TASK_STATUSES.map((s) => ({ value: s, label: TASK_STATUS_LABELS[s] }))}
            value={t.status} onChange={(v) => setTask(i, { status: v })} error={errors[`tasks[${i}].status`]} />
          {attendant && (
            <InputField {...common} id={`aide-task-${i}-minutes`} label="Minutes" type="number" inputMode="numeric" required={t.status === 'DONE'}
              value={t.minutes} onChange={(v) => setTask(i, { minutes: v })} error={errors[`tasks[${i}].minutes`]} />
          )}
          <button type="button" aria-label={`Remove task ${i + 1}`} disabled={disabled}
            className="mb-0.5 flex h-11 w-11 shrink-0 items-center justify-center rounded text-gray-500 hover:bg-gray-100 hover:text-red-600"
            onClick={() => onChange({ ...value, tasks: value.tasks.filter((_, j) => j !== i) })}>
            <Trash2 size={16} />
          </button>
          {(t.status === 'REFUSED' || t.note) && (
            <InputField {...common} id={`aide-task-${i}-note`} label={t.status === 'REFUSED' ? 'What happened' : 'Note'} required={t.status === 'REFUSED'}
              className={attendant ? 'md:col-span-4' : 'md:col-span-3'} value={t.note} onChange={(v) => setTask(i, { note: v })} error={errors[`tasks[${i}].note`]} />
          )}
        </div>
      ))}
      <button type="button" disabled={disabled} data-testid="aide-add-task"
        className="inline-flex items-center gap-1 rounded border border-lime-500 min-h-[44px] px-3 text-sm font-medium text-lime-800 hover:bg-lime-50"
        onClick={() => onChange({ ...value, tasks: [...value.tasks, { task: '', status: '', minutes: '', note: '' }] })}>
        <Plus size={14} /> Add task
      </button>
      {errors.tasks && <p className="text-xs text-red-600" data-testid="error-aide-tasks">{errors.tasks}</p>}

      <TextAreaField {...common} id="aide-observations" label="Observations for the supervising clinician" rows={2}
        placeholder="Changes in skin, breathing, intake or behavior; falls; equipment problems"
        value={value.observations} onChange={(v) => onChange({ ...value, observations: v })} />
      <Segmented name="aide-reported" label="Reported to the supervisor" isMobile={isMobile} disabled={disabled}
        options={[{ value: 'YES', label: 'Yes' }, { value: 'NO', label: 'No' }]}
        value={value.reportedToSupervisor ? 'YES' : 'NO'} onChange={(v) => onChange({ ...value, reportedToSupervisor: v === 'YES' })}
        showRequired={showRequired} />
    </div>
  );
};

export default AideTasksFields;
